/**
 * Live in-call transcript: the pure pieces shared by the server (Twilio
 * Real-Time Transcription webhook) and the browser (the AI Transcription panel).
 *
 * Twilio sends partial results for an utterance, then a final one. The panel
 * shows one bubble per utterance, so a partial replaces the previous partial of
 * the same speaker and a final replaces that partial and is never touched again.
 *
 * Track mapping for a browser call (transcription started on the agent's
 * client leg): inbound_track is what Twilio receives from the browser (the
 * agent), outbound_track is what Twilio sends to it (the contact).
 */

export type TranscriptSpeaker = 'agent' | 'contact';

export interface TranscriptLine {
  /** Stable per utterance: `${speaker}-${utteranceIndex}`. */
  id: string;
  speaker: TranscriptSpeaker;
  text: string;
  final: boolean;
  /** Twilio SequenceId (monotonic per transcription), used to drop stale partials. */
  seq: number;
  at: number;
}

export interface IncomingTranscriptChunk {
  speaker: TranscriptSpeaker;
  text: string;
  final: boolean;
  seq: number;
  at?: number;
}

export function trackToSpeaker(track: unknown): TranscriptSpeaker {
  return track === 'inbound_track' ? 'agent' : 'contact';
}

/** Merge one chunk into the list. Returns a new array; the input is not mutated. */
export function mergeTranscriptLine(lines: TranscriptLine[], chunk: IncomingTranscriptChunk): TranscriptLine[] {
  const text = (chunk.text || '').trim();
  if (!text) return lines;
  // The open (non-final) bubble of this speaker, if any.
  let openIdx = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (lines[i].speaker === chunk.speaker && !lines[i].final) { openIdx = i; break; }
  }
  if (openIdx >= 0) {
    const open = lines[openIdx];
    if (chunk.seq < open.seq) return lines; // out-of-order, older than what we show
    const next = lines.slice();
    next[openIdx] = { ...open, text, final: chunk.final, seq: chunk.seq };
    return next;
  }
  // A late duplicate of a final we already have.
  if (lines.some((l) => l.speaker === chunk.speaker && l.final && l.seq === chunk.seq)) return lines;
  const n = lines.filter((l) => l.speaker === chunk.speaker).length;
  return [...lines, { id: `${chunk.speaker}-${n}`, speaker: chunk.speaker, text, final: chunk.final, seq: chunk.seq, at: chunk.at ?? Date.now() }];
}

/** The stored transcript: final lines only, "Name: text" per line (same shape as post-call text). */
export function formatTranscriptForStorage(lines: TranscriptLine[], agentLabel: string, contactLabel: string): string {
  return lines
    .filter((l) => l.final && l.text.trim())
    .map((l) => `${l.speaker === 'agent' ? agentLabel : contactLabel}: ${l.text.trim()}`)
    .join('\n');
}

/**
 * Plan types that do NOT include live transcription. Every other plan does,
 * trials included: the product decision is "live transcription on all plans".
 * Only orgs with no plan (free / cancelled / none) are left out.
 */
export const NO_PLAN_TYPES = ['', 'free', 'none', 'cancelled', 'canceled', 'expired', 'inactive'] as const;

/** True when `planType` names a real plan (paid or trial). */
export function hasActivePlan(planType?: string | null): boolean {
  const plan = String(planType || '').trim().toLowerCase();
  return !(NO_PLAN_TYPES as readonly string[]).includes(plan) && !plan.startsWith('free');
}

/**
 * Live transcription costs money per minute, so it needs BOTH the platform
 * kill switch (env LIVE_TRANSCRIPTION_ENABLED=true) and the org's entitlement:
 * any active plan, or `settings.liveTranscription === true`.
 * An org can also opt out with `liveTranscription: false`.
 */
export function liveTranscriptionAllowed(o: { envEnabled: unknown; planType?: string | null; orgSetting?: unknown }): boolean {
  if (String(o.envEnabled ?? '').trim().toLowerCase() !== 'true') return false;
  if (o.orgSetting === false) return false;
  if (o.orgSetting === true) return true;
  return hasActivePlan(o.planType);
}
